import { useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { Loader2, Mail, RefreshCw } from 'lucide-react';
import { Button } from '@/shared/ui';
import { useAuth } from '@/features/auth/context/AuthContext';
import { AuthFooterBar, AuthTopBar, EditorialPanel } from '@/features/auth/components';

const ADMIN_EMAIL = 'admin@thegoldsmith.ai';

/**
 * Shown when the account is authenticated but not yet approved by a super admin.
 */
export default function AccountPendingPage() {
  const reduceMotion = useReducedMotion();
  const { logout, user, refreshWorkspaces } = useAuth();
  const [checking, setChecking] = useState(false);

  const handleCheckAgain = async () => {
    setChecking(true);
    try {
      await refreshWorkspaces();
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-canvas text-ink-900">
      <EditorialPanel />

      <section className="flex-1 flex flex-col bg-surface relative">
        <AuthTopBar />

        <div className="flex-1 flex items-center justify-center px-5 sm:px-10 lg:px-14 safe-pt safe-pb">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-[400px] py-8 sm:py-10"
          >
            <header className="mb-9">
              <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-3">
                On the waitlist
              </p>
              <h1 className="font-headline text-[34px] leading-[1.1] text-ink-900">
                You are enrolled for early access.
              </h1>
              <p className="mt-2.5 text-[14.5px] text-ink-500 leading-relaxed">
                Your application is in the queue. We will email you when your studio is approved. For a faster
                response, reach out directly. We read every message.
              </p>
            </header>

            <div className="rounded-lg bg-amber-50/90 border border-amber-200/80 px-4 py-3 text-[13px] text-amber-950/90 leading-relaxed mb-5">
              You are signed in, but the studio stays locked until we approve your waitlist application. Use
              &quot;Check again&quot; once you have been approved.
            </div>

            <div className="space-y-3.5">
              <motion.a
                href={`mailto:${ADMIN_EMAIL}?subject=Account%20access%20request`}
                whileHover={reduceMotion ? undefined : { y: -1 }}
                whileTap={{ scale: 0.99 }}
                className="btn-press btn-shine w-full h-12 rounded-xl bg-ink-900 hover:bg-ink-800 text-white font-semibold text-[14px] tracking-wide flex items-center justify-center gap-2 cursor-pointer focus-ring"
              >
                <Mail size={16} strokeWidth={2.2} />
                <span className="tabular">{ADMIN_EMAIL}</span>
              </motion.a>

              <Button
                variant="secondary"
                fullWidth
                disabled={checking}
                onClick={() => void handleCheckAgain()}
                leadingIcon={
                  checking ? <Loader2 size={16} className="animate-spin shrink-0" /> : <RefreshCw size={16} strokeWidth={2.2} />
                }
              >
                {checking ? 'Checking…' : 'I was approved. Check again'}
              </Button>

              <p className="text-center text-[13px] text-ink-500 pt-2">
                <button
                  type="button"
                  onClick={() => logout()}
                  className="font-semibold text-ink-800 hover:text-gold-600 transition-colors cursor-pointer"
                >
                  Sign out
                  {user?.email ? ` (${user.email})` : ''}
                </button>
              </p>
            </div>
          </motion.div>
        </div>

        <AuthFooterBar />
      </section>
    </div>
  );
}
